import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { History } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { TablePaginationBar } from '@/components/app/TablePaginationBar'
import { cn } from '@/lib/utils'
import { fetchSyncJobs } from '../api'

function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(d)
}

function statusToneClass(status: string) {
  const s = status.toLowerCase()
  if (s === 'failed' || s === 'dead_lettered') {
    return 'border-transparent bg-rose-500/10 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'
  }
  if (s === 'completed' || s === 'success') {
    return 'border-transparent bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
  }
  if (s === 'pending' || s === 'queued') {
    return 'border-transparent bg-sky-500/10 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300'
  }
  return 'border-transparent bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
}

export function SyncIncidentsTable() {
  const { t, i18n } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const jobsQuery = useQuery({
    queryKey: ['sync', 'jobs', 'failed'],
    queryFn: () => fetchSyncJobs({ status: 'failed' }),
    select: (res) => res.data,
  })

  const rows = useMemo(() => {
    if (!jobsQuery.data) return []
    return [...jobsQuery.data].sort(
      (a, b) => new Date(b.failed_at ?? b.queued_at ?? 0).getTime() - new Date(a.failed_at ?? a.queued_at ?? 0).getTime(),
    )
  }, [jobsQuery.data])

  const totalItems = rows.length
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const pageSafe = Math.min(page, totalPages)

  const pageSlice = useMemo(() => {
    const start = (pageSafe - 1) * pageSize
    return rows.slice(start, start + pageSize)
  }, [rows, pageSafe, pageSize])

  if (jobsQuery.isPending) {
    return (
      <div className="overflow-hidden rounded-lg border border-border">
        <div className="space-y-0">
          <Skeleton className="h-10 w-full rounded-none" />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-none border-t border-border/50" />
          ))}
        </div>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="flex items-center gap-3.5 bg-card px-4 py-5">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600">
          <History className="size-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {t('settings.sync.noIncidents')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('settings.sync.noIncidentsDescription')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.sync.table.type')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.sync.table.status')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.sync.table.error')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.sync.table.queue')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-right text-xs font-medium text-muted-foreground">
                {t('settings.sync.table.failedAt')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageSlice.map((job) => {
              const isDeadLettered = job.status === 'dead_lettered'
              const statusLabel = isDeadLettered
                ? t('settings.sync.status.deadLettered')
                : t('settings.sync.status.failed')

              return (
                <TableRow key={job.id} className="group">
                  <TableCell className="py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="text-sm text-foreground capitalize">
                        {t(`settings.sync.jobTypes.${job.job_type.replace(/\./g, '_')}`, {
                          defaultValue: job.job_type.replace(/_/g, ' '),
                        })}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t(`settings.sync.entityTypes.${job.entity_type}`, {
                          defaultValue: job.entity_type,
                        })}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5">
                    <Badge
                      variant="outline"
                      className={cn(
                        'rounded-full text-[11px] font-medium capitalize',
                        statusToneClass(job.status),
                      )}
                    >
                      {statusLabel}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2.5 text-sm text-muted-foreground">
                    <div className="max-w-[16rem] truncate" title={job.failure_message ?? undefined}>
                      {job.failure_code ? (
                        <span className="font-mono text-xs text-rose-600">{job.failure_code}</span>
                      ) : null}
                      {job.failure_code && job.failure_message ? ' · ' : null}
                      {job.failure_message ?? '—'}
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 text-sm text-muted-foreground">
                    {job.queue_name}
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-sm tabular-nums text-muted-foreground">
                    {formatDateTime(job.failed_at ?? job.queued_at, i18n.language)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      <TablePaginationBar
        page={pageSafe}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
        pageSizeOptions={[5, 10, 25, 50]}
      />
    </div>
  )
}
